/**
 * BACKEND_TODO: Fields needed on v2 list endpoints for full UX parity
 *
 * Upcoming list (GET /live-event/upcoming):
 * - statusName: ✅ now returned by API
 * - eventTime: ✅ now returned by API
 *
 * Past list (GET /live-event/past):
 * - None blocking (modal now drives off attendeesCount)
 */

import {readWireMinimumPrice} from "@/features/pricing";
import type {
  LiveEvent,
  LiveEventSession,
  LiveEventCurrency,
  CoverImage,
  BookingQuestion,
  PaginatedLiveEvents,
  CreateLiveEventRequest,
  LiveEventStatus,
  JoinLiveEventResult,
  RescheduleLiveEventRequest,
  LiveEventListItem,
} from "../../domain/types";
import type {
  LiveEventV2Response,
  LiveEventListV2Data,
  LiveEventV2Request,
  SessionInfoV2,
  CurrencyV2,
  CoverImageV2,
  BookingQuestionV2,
  JoinLiveEventV2Response,
  RescheduleV2Request,
  LiveEventUpcomingListItemV2,
  LiveEventPastListItemV2,
  LiveEventUpcomingListV2Data,
  LiveEventPastListV2Data,
} from "./mapper.types";

/**
 * Maps v2 API responses to domain types.
 * V2 API uses camelCase fields matching domain.
 *
 * Pure TypeScript - no React imports.
 */
export const LiveEventMapperV2 = {
  /**
   * Maps v2 session info to domain session.
   */
  toSession(session: SessionInfoV2): LiveEventSession {
    return {
      name: session.name,
      startDate: session.startDate,
      startTime: session.startTime,
      endTime: session.endTime,
      duration: session.duration,
    };
  },

  /**
   * Maps v2 currency to domain currency.
   * Note: API uses "amount", domain uses "price".
   *
   * `minAmount` is the wire's spelling of the Minimum Price; `minCallPrice` is the
   * domain's. The rename stops here.
   */
  toCurrency(currency: CurrencyV2): LiveEventCurrency {
    return {
      id: currency.id,
      currency: currency.currency,
      price: currency.amount,
      minCallPrice: readWireMinimumPrice(currency.minAmount),
      isAutoConverted: currency.isAutoConverted,
      isBase: currency.isBase,
    };
  },

  /**
   * Maps v2 cover image to domain cover image.
   */
  toCoverImage(image: CoverImageV2): CoverImage {
    return {
      id: image.id,
      mediaUrl: image.mediaUrl,
      createdAt: image.createdAt,
    };
  },

  /**
   * Maps v2 booking question to domain booking question.
   * Note: V2 API also uses answer_type (snake_case) in responses.
   */
  toBookingQuestion(question: BookingQuestionV2): BookingQuestion {
    return {
      question: question.question,
      answerType: (question.answer_type || "short-text") as BookingQuestion["answerType"],
      required: question.required,
      options: question.options,
    };
  },

  /**
   * Maps v2 live event response to domain LiveEvent.
   */
  toDomain(response: LiveEventV2Response): LiveEvent {
    return {
      id: response.id,
      userId: response.userId,
      name: response.name,
      slug: response.slug,
      description: response.description,
      topicType: response.topicType,
      videoPlatform: response.videoPlatform,
      roomId: response.roomId,
      callPrice: response.callPrice,
      callPriceCurrency: response.callPriceCurrency,
      currencies: (response.currencies || []).map(LiveEventMapperV2.toCurrency),
      isPayWhatYouWant: response.isPayWhatYouWant ?? false,
      attendees: response.attendees,
      statusName: response.statusName as LiveEventStatus,
      reference: response.reference,
      tinyUrl: response.tinyUrl,
      coverImage: response.coverImage || "",
      coverImages: (response.coverImages || []).map(LiveEventMapperV2.toCoverImage),
      sessionInfo: (response.sessionInfo || []).map(LiveEventMapperV2.toSession),
      sessionCount: response.sessionCount,
      activeSession: response.activeSession
        ? LiveEventMapperV2.toSession(response.activeSession)
        : null,
      activeSessionId: response.activeSessionId,
      bookingQuestions: (response.bookingQuestions || []).map(
        LiveEventMapperV2.toBookingQuestion
      ),
      requestPhoneNumber: response.requestPhoneNumber,
      purchaseLimit: response.purchaseLimit,
      hideFromMenu: response.hideFromMenu,
      isPaused: response.isPaused ?? false,
      urlRedirect: response.urlRedirect ?? null,
      redirectType: response.redirectType,
      telegramGroupChatId: response.telegramGroupChatId,
      hasCustomLink: response.hasCustomLink ?? false,
      customLink: response.customLink ?? null,
      isCustomRoom: response.isCustomRoom,
      hasGoogleMeet:
        response.hasGoogleMeet === true ||
        response.videoPlatform?.toLowerCase() === "googlemeet",
      locationAddress: response.locationAddress ?? null,
      locationLat: response.locationLat ?? null,
      locationLng: response.locationLng ?? null,
      isLocationPublic: response.isLocationPublic ?? true,
      enableCurrencyConversion: response.enableCurrencyConversion ?? false,
      bookedCounts: response.bookedCounts,
      createdAt: response.createdAt,
      updatedAt: response.updatedAt,
    };
  },

  /**
   * @deprecated Use toDomainUpcomingList or toDomainPastList instead.
   * Maps v2 paginated list response to domain PaginatedLiveEvents.
   * Accepts the inner data (after extracting from API wrapper).
   */
  toDomainList(response: LiveEventListV2Data): PaginatedLiveEvents {
    return {
      data: response.data.map((item) =>
        LiveEventMapperV2.toDomainUpcomingListItem(
          item as unknown as LiveEventUpcomingListItemV2
        )
      ),
      total: response.total,
      hasNextPage: response.hasNextPage,
      hasPrevPage: response.hasPrevPage,
    };
  },

  /**
   * Maps v2 upcoming list item to domain LiveEventListItem.
   */
  toDomainUpcomingListItem(item: LiveEventUpcomingListItemV2): LiveEventListItem {
    return {
      id: item.id,
      name: item.name,
      description: item.description,
      hasPhysicalLocation: item.videoPlatform?.toLowerCase() === "physicallocation",
      locationLat: item.locationLat ?? null,
      locationLng: item.locationLng ?? null,
      callPrice: item.amount,
      callPriceCurrency: item.currency,
      coverImage: item.imageUrl ?? "",
      eventDate: item.eventDate,
      eventTime: item.eventTime,
      attendeesCount: item.attendeesCount ?? 0,
      statusName: (item.statusName as LiveEventStatus) ?? "Active",
      createdAt: "",
    };
  },

  /**
   * Maps v2 past list item to domain LiveEventListItem.
   */
  toDomainPastListItem(item: LiveEventPastListItemV2): LiveEventListItem {
    return {
      id: item.id,
      name: item.name,
      description: item.description,
      callPrice: item.amount,
      callPriceCurrency: item.currency,
      coverImage: "",
      eventDate: "",
      attendeesCount: item.attendeesCount ?? 0,
      statusName: (item.statusName as LiveEventStatus) ?? "Completed",
      createdAt: item.createdAt,
    };
  },

  /**
   * Maps v2 upcoming list response to domain PaginatedLiveEvents.
   */
  toDomainUpcomingList(response: LiveEventUpcomingListV2Data): PaginatedLiveEvents {
    return {
      data: response.data.map(LiveEventMapperV2.toDomainUpcomingListItem),
      total: response.total,
      hasNextPage: response.hasNextPage,
      hasPrevPage: response.hasPrevPage,
    };
  },

  /**
   * Maps v2 past list response to domain PaginatedLiveEvents.
   */
  toDomainPastList(response: LiveEventPastListV2Data): PaginatedLiveEvents {
    return {
      data: response.data.map(LiveEventMapperV2.toDomainPastListItem),
      total: response.total,
      hasNextPage: response.hasNextPage,
      hasPrevPage: response.hasPrevPage,
    };
  },

  /**
   * Maps domain request to v2 API request format.
   * Handles both create (full) and update (partial) requests.
   * Note: v2 API uses mixed case - topic_type, session_info, answer_type are snake_case.
   */
  toApiRequest(request: Partial<CreateLiveEventRequest>): Partial<LiveEventV2Request> {
    const result: Partial<LiveEventV2Request> = {};

    if (request.name !== undefined) result.name = request.name;
    if (request.description !== undefined) result.description = request.description;
    if (request.topicType !== undefined) result.topic_type = request.topicType;
    if (request.sessionInfo !== undefined) {
      result.session_info = request.sessionInfo.map((s) => ({
        name: s.name,
        startDate: s.startDate,
        startTime: s.startTime,
        duration: s.duration,
      }));
    }
    if (request.callPrice !== undefined) result.callPrice = request.callPrice;
    if (request.callPriceCurrency !== undefined)
      result.callPriceCurrency = request.callPriceCurrency;
    // Only Pay What You Want has a Minimum Price; the key is omitted rather than sent as
    // a zero the coach never typed.
    if (request.minCallPrice !== undefined) result.minCallPrice = request.minCallPrice;
    if (request.isPayWhatYouWant !== undefined)
      result.isPayWhatYouWant = request.isPayWhatYouWant;
    if (request.currencies !== undefined) {
      // The base currency stays in the scalars above and must not be repeated here.
      result.currencies = request.currencies.map((c) => ({
        callPriceCurrency: c.callPriceCurrency,
        callPrice: c.callPrice,
        ...(c.minCallPrice === undefined ? {} : {minCallPrice: c.minCallPrice}),
      }));
    }
    if (request.attendees !== undefined) result.attendees = request.attendees;
    if (request.bookingQuestions !== undefined) {
      result.bookingQuestions = request.bookingQuestions.map((q) => ({
        question: q.question,
        answer_type: q.answerType,
        required: q.required,
        options: q.options,
      }));
    }
    if (request.requestPhoneNumber !== undefined)
      result.requestPhoneNumber = request.requestPhoneNumber;
    // Omitted outright when the coach set no cap — see `toPurchaseLimitPayload`.
    if (request.purchaseLimit !== undefined) result.purchaseLimit = request.purchaseLimit;
    if (request.hideFromMenu !== undefined) result.hideFromMenu = request.hideFromMenu;
    if (request.isPaused !== undefined) result.isPaused = request.isPaused;
    if (request.urlRedirect !== undefined) result.urlRedirect = request.urlRedirect;
    // The API's enum is `url | telegram`; no redirect goes as `null`, which also clears a
    // saved one on update.
    if (request.redirectType !== undefined)
      result.redirectType = request.redirectType === "none" ? null : request.redirectType;
    if (request.telegramGroupChatId !== undefined)
      result.telegramGroupChatId = request.telegramGroupChatId;
    if (request.hasCustomLink !== undefined) result.hasCustomLink = request.hasCustomLink;
    if (request.customLink !== undefined) result.customLink = request.customLink;
    if (request.useZoom !== undefined) result.useZoom = request.useZoom;
    if (request.hasGoogleMeet !== undefined) result.hasGoogleMeet = request.hasGoogleMeet;
    if (request.hasPhysicalLocation !== undefined)
      result.hasPhysicalLocation = request.hasPhysicalLocation;
    if (request.locationAddress !== undefined)
      result.locationAddress = request.locationAddress;
    if (request.locationLat !== undefined) result.locationLat = request.locationLat;
    if (request.locationLng !== undefined) result.locationLng = request.locationLng;
    if (request.isLocationPublic !== undefined)
      result.isLocationPublic = request.isLocationPublic;
    if (request.enableCurrencyConversion !== undefined)
      result.enableCurrencyConversion = request.enableCurrencyConversion;
    if (request.coverImage !== undefined) result.coverImage = request.coverImage;

    return result;
  },

  /**
   * Maps v2 join response to domain JoinLiveEventResult.
   * V2 shape matches domain - passthrough with nullish coalescing.
   */
  joinToDomain(response: JoinLiveEventV2Response): JoinLiveEventResult {
    return {
      token: response.token,
      roomId: response.roomId,
      videoPlatform: response.videoPlatform,
      customRoomLink: response.customRoomLink ?? null,
      dailyCoUrl: response.dailyCoUrl ?? null,
    };
  },

  /**
   * Maps domain reschedule request to v2 API format.
   * V2 accepts the full sessionInfo array with top-level reason.
   * Strips eventId (goes in URL) and previousSessionInfo (only needed for v1 diff).
   */
  rescheduleToV2Request(request: RescheduleLiveEventRequest): RescheduleV2Request {
    return {
      reason: request.reason,
      sessionInfo: request.sessionInfo.map((s) => ({
        name: s.name,
        startDate: s.startDate,
        startTime: s.startTime,
        duration: s.duration,
      })),
    };
  },
};
